import { Dialog, PlainButton, Button, Alert } from "@jugl-web/ui-components";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useState, useCallback, useEffect } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { hasRoleAccessToModule } from "@jugl-web/domain-resources/users/utils/hasRoleAccessToModule";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { AppModule } from "@jugl-web/domain-resources/common/types";
import { useUserEntityPreferences } from "@jugl-web/domain-resources/preferences/hooks/useUserEntityPreferences";
import { AppMenuItem } from "@jugl-web/rest-api";
import { isEqual } from "lodash";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { ReactComponent as HeaderIcon } from "./assets/header.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as HelpIcon } from "./assets/help.svg";
import mainImage from "./assets/main.png";
import { MenuSetter } from "./components/MenuSetter/MenuSetter";
import { useHomeSidebar } from "../../hooks/useHomeSidebar";
import alertAnimation from "./assets/alert-animation.json";

export const PersonalizeMenuDialog = ({
  isOpen,
  onClose = () => {},
}: {
  isOpen: boolean;
  onClose?: () => void;
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entity } = useEntitySelectedProvider();
  const { preferences, updatePreferences } = useUserEntityPreferences({
    entityId: entity.id,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const { pinnedTools: pinnedMenuTools } = useHomeSidebar();

  const [isDicardChangesAlertOpen, setIsDicardChangesAlertOpen] =
    useState(false);
  const [pinnedTools, setPinnedTools] = useState<AppMenuItem[]>([]);

  const { navigateToPage } = useNavigation();

  const handleOnSave = async () => {
    setIsLoading(true);
    try {
      await updatePreferences({
        pinned_modules: { pinnedTools },
      });

      toast(
        t({
          id: "personalize-menu-dialog.personalised-menu-updates-saved",
          defaultMessage: "Personalised menu updates was successfully saved",
        })
      );
      handleClose();
    } catch {
      setIsLoading(false);
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  };

  const handlePinButtonClick = useCallback(
    (e: React.SyntheticEvent, itemKey: AppMenuItem) => {
      e.preventDefault();
      e.stopPropagation();
      setPinnedTools(
        pinnedTools.includes(itemKey)
          ? pinnedTools.filter((key) => key !== itemKey)
          : [...pinnedTools, itemKey]
      );
    },
    [pinnedTools]
  );

  const handleDragEnd = (result: DropResult) => {
    // Early returns for invalid drag operations
    if (
      !result.destination ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    // Boundary checks to ensure indices are valid
    if (
      result.source.index < 0 ||
      result.destination.index < 0 ||
      result.source.index >= pinnedTools.length ||
      result.destination.index >= pinnedTools.length
    ) {
      return;
    }

    const reorderedPinnedTools = Array.from(pinnedTools);
    const [removed] = reorderedPinnedTools.splice(result.source.index, 1);
    reorderedPinnedTools.splice(result.destination.index, 0, removed);

    setPinnedTools(reorderedPinnedTools);
  };

  const handleClose = () => {
    setIsDicardChangesAlertOpen(false);
    onClose();
    setHasInitialized(false);
  };

  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleSaveClose = () => {
    if (
      !isEqual(
        pinnedMenuTools.map(({ itemKey }) => itemKey),
        pinnedTools
      )
    ) {
      setIsDicardChangesAlertOpen(true);
      return;
    }

    handleClose();
  };

  useEffect(() => {
    if (!hasInitialized && preferences && isOpen) {
      setPinnedTools(pinnedMenuTools.map(({ itemKey }) => itemKey));
      setHasInitialized(true);
    }
  }, [hasInitialized, pinnedMenuTools, preferences, isOpen]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={handleSaveClose}
        className="font-secondary z-[1398] flex h-[783px] !max-h-[min(95vh,783px)] w-[1310px]
    max-w-[min(95vw,1310px)] origin-center flex-col overflow-auto rounded-2xl"
      >
        <div className="border-dark-100 flex h-[60px] w-full shrink-0 items-center justify-between border-0 border-b-[1px] border-solid px-6">
          <div className="flex items-center gap-3 text-lg font-semibold text-[#383838]">
            <HeaderIcon />
            {t({
              id: "personalize-menu-dialog.title",
              defaultMessage: "Personalise Menu",
            })}
          </div>
          <PlainButton
            onClick={handleSaveClose}
            className="rounded-lg hover:brightness-90"
          >
            <CloseIcon />
          </PlainButton>
        </div>
        <div className="flex h-full w-full gap-10 overflow-auto px-10 py-8">
          <img
            src={mainImage}
            alt="Main"
            className="hidden h-full rounded-3xl [@media(min-width:1200px)]:block"
          />
          <MenuSetter
            pinnedTools={pinnedTools}
            onPinClick={handlePinButtonClick}
            onDragEnd={handleDragEnd}
          />
        </div>
        <div className="border-dark-100 flex h-[77px] w-full shrink-0 items-center justify-between border-0 border-t-[1px] border-solid px-10">
          {hasRoleAccessToModule(entity.role, AppModule.workspace) ? (
            <div className="text-dark-700 flex items-center gap-1 text-xs">
              <HelpIcon className="mr-1" />
              {t(
                {
                  id: "personalize-menu-dialog.missing-features",
                  defaultMessage:
                    "Missing some features? Go to <text>Workspace Features</text>",
                },
                {
                  text: (text: (string | JSX.Element)[]) => (
                    <div
                      onClick={() => {
                        handleSaveClose();
                        navigateToPage("workspaceFeatures");
                      }}
                      className="text-primary-500 cursor-pointer font-medium underline"
                    >
                      {text}
                    </div>
                  ),
                }
              )}
            </div>
          ) : (
            <div />
          )}
          <Button size="small" onClick={handleOnSave} isDisabled={isLoading}>
            {t({
              id: "personalize-menu-dialog.save-changes",
              defaultMessage: "Save changes",
            })}
          </Button>
        </div>
      </Dialog>
      <Alert
        isOpen={isDicardChangesAlertOpen}
        onRequestClose={() => setIsDicardChangesAlertOpen(false)}
        className="z-[1400]"
        backdropClassName="z-[1399]"
        img={
          <Lottie
            options={{ animationData: alertAnimation }}
            width={70}
            height={70}
          />
        }
        title={t({
          id: "personalize-menu-dialog.dicard-changes",
          defaultMessage: "Discard changes",
        })}
        content={t({
          id: "personalize-menu-dialog.discard-changes-warning",
          defaultMessage: "If you discard, entered info will not be saved",
        })}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            role: "close",
          },
          {
            text: t({
              id: "common.discard",
              defaultMessage: "Discard",
            }),
            color: "tertiary",
            onClick: handleClose,
          },
        ]}
      />
    </>
  );
};
